import { AvatarProps, Container, Stack, Typography } from '@mui/material';
import React from 'react';

import './VideoContainer.scss';

type Props = AvatarProps<React.ElementType, { component?: React.ElementType }> & {
	videoUrl?: string;
	videoTitle?: string;
	videoSubtitle?: string;
	actionButtons?: any[];
};

type VideoFormat = 'YouTube' | 'mp4' | 'webm';

type CompanyVideo = {
	videoFormat: VideoFormat;
	youTubeVideoId?: string;
	videoUrl?: string;
};

export default class VideoContainer extends React.Component<Props> {
	/**
	 * Extracts the YouTube video ID from a given URL.
	 * @param url The YouTube video URL.
	 * @returns The YouTube video ID or undefined if the URL is invalid.
	 */
	extractYouTubeVideoId = (url: string): string | undefined => {
		const pattern = new RegExp(
			[
				// Protocol (optional)
				'(?:https?:\\/\\/)?',

				// Subdomain www (optional)
				'(?:www\\.)?',

				// Domain name and path
				'(?:',
				'youtube\\.com\\/',
				'(?:',
				// Various YouTube paths
				'[^\\/\\n\\s]+\\/\\s*[\\S]+\\/', // For user paths and alike
				'|(?:v|e(?:mbed)?)\\/', // For /v/ and /embed/ paths
				'|watch\\?.*?v=', // For standard watch URLs
				'|user\\/.+?#p\\/.+?\\/.+?\\/', // For user page URLs
				')',
				'|youtu\\.be\\/', // For shortened URLs
				'|youtube\\.com\\/shorts\\/', // For YouTube shorts
				')',

				// Video ID
				'([-_0-9A-Za-z]{11})', // The actual video ID
			].join('')
		);
		const match = RegExp(pattern).exec(url);
		return match ? match[1] : undefined;
	};

	parseVideoUrl = (url: string | undefined): CompanyVideo | undefined => {
		if (!url) {
			return undefined;
		}

		// test for YouTube video URL
		const ytVideoId = this.extractYouTubeVideoId(url);
		if (ytVideoId) {
			return {
				videoFormat: 'YouTube',
				youTubeVideoId: ytVideoId,
			};
		} else if (url.endsWith('.mp4')) {
			return {
				videoFormat: 'mp4',
				videoUrl: url,
			};
		} else if (url.endsWith('.webm')) {
			return {
				videoFormat: 'webm',
				videoUrl: url,
			};
		} else {
			return undefined;
		}
	};

	parseImageUrl = (url: string | undefined): string | undefined => {
		const defaultImageUrl = '/company-video-fallback-1.jpg';
		if (!url) {
			return defaultImageUrl;
		}

		let filename = url.split('/').pop();
		// strip query string
		filename = filename?.split('?')[0] ?? filename;

		if (
			filename?.endsWith('.jpg') ||
			filename?.endsWith('.jpeg') ||
			filename?.endsWith('.png') ||
			filename?.endsWith('.webp')
		) {
			return url;
		}

		return defaultImageUrl;
	};

	renderBackground() {
		const companyVideo: CompanyVideo | undefined = this.parseVideoUrl(this.props.videoUrl);
		const imageUrl = this.parseImageUrl(this.props.videoUrl);

		const isYoutubeVideo = companyVideo?.videoFormat == 'YouTube' && companyVideo?.youTubeVideoId;

		if (!companyVideo || isYoutubeVideo) {
			return (
				<img
					className="promo-video"
					src={imageUrl}
					alt="Befare"
					// style={{ width: '100%', height: '100%' }}
				/>
			);
			// @todo: BEF-561	do not remove this section. it's unused because the YT embed player is non-responsive.
			// @todo: BEF-561	Add support for verticalYouTubeVideo and horizontalYouTubeVideo
			// } else if (isYoutubeVideo) {
			// 	return (
			// 		<iframe
			// 			className="promo-video youtube-video"
			// 			src={
			// 				// see https://developers.google.com/youtube/player_parameters
			// 				`https://www.youtube.com/embed/${companyVideo.youTubeVideoId}?` +
			// 				`&autoplay=1` + // autoplays
			// 				`&mute=1` + // muted
			// 				`&loop=1` + // loops forever
			// 				`&controls=0` + // control ui hidden
			// 				`&showinfo=0` + // titlebar ui hidden
			// 				`&playsinline=1` + // plays inline on iOS devices
			// 				`&rel=0` + // hides related videos (in case paused or ended)
			// 				`&fs=0` + // hides fullscreen button (in case controls=0 fails)
			// 				`&iv_load_policy=3` + // hides video annotations
			// 				`&disablekb=1` + // disables keyboard controls
			// 				`&color=white` // color of the progress bar (in case controls=0 fails)
			// 			}
			// 			allowFullScreen
			// 			title={this.props.videoTitle}
			// 			allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
			// 		></iframe>
			// 	);
		} else if (companyVideo.videoFormat == 'mp4' || companyVideo.videoFormat == 'webm') {
			return (
				<video
					className="promo-video"
					src={companyVideo.videoUrl}
					poster=""
					autoPlay={true}
					muted={true}
					loop={true}
					playsInline={true}
					data-controller="video"
				></video>
			);
		}
	}

	render() {
		return (
			<Container className="VideoContainer">
				<div className="video-container">{this.renderBackground()}</div>

				<Stack
					className="overlay-container"
					direction="column"
					spacing={2}
					justifyContent="end"
					alignContent="center"
					marginBottom="20px"
				>
					{this.props.videoTitle && (
						<Typography
							variant="h2"
							component="span"
							sx={(theme) => ({
								color: theme.palette.primary.contrastText,
								textAlign: 'center',
								fontWeight: 'bold',
								textShadow: '2px 2px 4px #000000',
								[theme.breakpoints.down('sm')]: {
									fontSize: '2rem',
								},
							})}
						>
							{this.props.videoTitle}
						</Typography>
					)}
					{this.props.videoSubtitle && (
						<Typography
							variant="h4"
							component="span"
							sx={(theme) => ({
								color: theme.palette.primary.contrastText,
								textAlign: 'center',
								textShadow: '2px 2px 4px #000000',
								[theme.breakpoints.down('sm')]: {
									fontSize: '1.1rem',
								},
							})}
						>
							{this.props.videoSubtitle}
						</Typography>
					)}
					{/* <h1 className="video-title">{this.props.videoTitle}</h1>
					<h2 className="video-subtitle">{this.props.videoSubtitle}</h2> */}
					<Stack className="actionButtons-container" direction="row" spacing={1} justifyContent="center">
						{this.props.actionButtons?.map((button, index) => (
							<div key={`button-${index}`}>{button}</div> // Render each button with a unique key
						))}
					</Stack>
				</Stack>

				{/* {JSON.stringify(this.props)} */}
			</Container>
		);
	}
}
